import { CLIENTS_FETCH_PAGE_SIZE } from '../constants/clients';
import type {
  IClientApiItem,
  IClientContact,
  IClientContactsResponse,
  IClientsResponse,
  IClientPlanApi,
  IClientPlansResponse
} from '../interfaces/clients';
import { ClientContactType } from '../types/clients';
import { addDays, formatDateISO } from '../utils/dates/dates';

export const CLIENTS_API_MOCK: IClientApiItem[] = [
  {
    id: 1,
    countryCode: 'CL',
    documentType: 'RUT',
    documentValue: '76.123.456-7',
    companyName: 'Tech Solutions SpA',
    businessName: 'Tech Solutions SpA',
    companySize: 'sme',
    clientType: 'company',
    city: 'Santiago',
    state: 'Región Metropolitana',
    activationDate: '2026-07-01',
    prospectId: 1,
    workforceSize: 50,
    industry: 'Technology'
  },
  {
    id: 2,
    countryCode: 'CL',
    documentType: 'RUT',
    documentValue: '78.654.321-K',
    companyName: 'Constructora Del Valle Ltda',
    companySize: 'corporate',
    clientType: 'company',
    city: 'Valparaíso',
    state: 'Región de Valparaíso',
    activationDate: '2026-06-15',
    prospectId: 2,
    workforceSize: 120,
    industry: 'Construction'
  },
  {
    id: 3,
    countryCode: 'CL',
    documentType: 'RUT',
    documentValue: '99.001.002-3',
    companyName: 'Retail Express S.A.',
    companySize: 'enterprise',
    clientType: 'company',
    city: 'Concepción',
    state: 'Región del Biobío',
    activationDate: '2026-05-28',
    prospectId: 3,
    workforceSize: 310,
    industry: 'Retail'
  },
  {
    id: 4,
    countryCode: 'CL',
    documentType: 'RUT',
    documentValue: '77.999.888-1',
    companyName: 'Alpha Services Inc.',
    companySize: 'sme',
    clientType: 'company',
    city: 'Santiago',
    state: 'Región Metropolitana',
    activationDate: '2026-05-04',
    prospectId: 4,
    workforceSize: 60,
    industry: 'Professional Services'
  },
  {
    id: 5,
    countryCode: 'CL',
    documentType: 'RUT',
    documentValue: '75.111.222-3',
    companyName: 'Clínica Andes SpA',
    companySize: 'corporate',
    clientType: 'company',
    city: 'Santiago',
    state: 'Región Metropolitana',
    activationDate: '2026-04-19',
    prospectId: 5,
    workforceSize: 210,
    industry: 'Healthcare'
  },
  {
    id: 6,
    countryCode: 'CL',
    documentType: 'RUT',
    documentValue: '76.987.654-3',
    companyName: 'Transportes Norte Ltda',
    companySize: 'sme',
    activity: 'Transportation',
    clientType: 'company',
    activationDate: '2026-03-30'
  },
  {
    id: 7,
    countryCode: 'CL',
    documentType: 'RUT',
    documentValue: '80.123.456-5',
    companyName: 'Green Foods S.A.',
    companySize: 'corporate',
    clientType: 'company',
    city: 'Santiago',
    state: 'Región Metropolitana',
    activationDate: '2026-03-02',
    prospectId: 7,
    workforceSize: 145,
    industry: 'Food & Beverage'
  },
  {
    id: 8,
    countryCode: 'CL',
    documentType: 'RUT',
    documentValue: '81.654.321-7',
    companyName: 'Educa Global SpA',
    companySize: 'sme',
    clientType: 'company',
    city: 'Santiago',
    state: 'Región Metropolitana',
    activationDate: '2026-02-11',
    prospectId: 8,
    workforceSize: 95,
    industry: 'Education'
  },
  {
    id: 9,
    countryCode: 'CL',
    documentType: 'RUT',
    documentValue: '82.111.222-9',
    companyName: 'Minera Atacama Ltda',
    companySize: 'enterprise',
    clientType: 'company',
    city: 'Calama',
    state: 'Región de Antofagasta',
    activationDate: '2026-01-20',
    prospectId: 9,
    workforceSize: 420,
    industry: 'Mining'
  }
];

export const GET_CLIENTS_MOCK_RESPONSE = (): Promise<{ data: IClientsResponse }> =>
  Promise.resolve({
    data: {
      pagination: { pageNumber: 1, pageSize: CLIENTS_FETCH_PAGE_SIZE, total: CLIENTS_API_MOCK.length },
      clients: CLIENTS_API_MOCK
    }
  });

const relDate = (days: number): string => formatDateISO(addDays(new Date(), days));

export const CLIENT_PLANS_MOCK: IClientPlanApi[] = [
  {
    id: 8842,
    contractId: 5107,
    commercialPlanId: 1,
    name: 'Group Term Life',
    description: 'Group Term Life coverage for employees',
    fantasyName: 'Vida Colectiva',
    productType: 'life',
    status: 'active',
    price: 45,
    currencyCode: 'USD',
    coBranded: true,
    numberOfLicenses: 120,
    quoteId: 1387,
    contractStartDate: relDate(-90),
    addons: [
      {
        id: 33012,
        commercialAddonId: 2383,
        name: 'Telemedicine',
        description: '24/7 virtual doctor visits',
        price: 6.5,
        currencyCode: 'USD',
        startDate: relDate(-90)
      },
      {
        id: 33095,
        commercialAddonId: 2461,
        name: 'Dental Rider',
        price: 4,
        currencyCode: 'USD',
        startDate: relDate(30),
        endDate: relDate(400)
      }
    ]
  },
  {
    id: 8850,
    contractId: 5107,
    commercialPlanId: 640,
    name: 'Dental Care',
    productType: 'dental',
    status: 'active',
    price: 12,
    currencyCode: 'USD',
    coBranded: false,
    numberOfLicenses: 120,
    quoteId: 1387,
    contractStartDate: relDate(-5),
    addons: []
  },
  {
    id: 8861,
    contractId: 5210,
    commercialPlanId: 655,
    name: 'Vision Plus',
    productType: 'vision',
    status: 'active',
    price: 9,
    currencyCode: 'USD',
    coBranded: false,
    numberOfLicenses: 80,
    quoteId: 1402,
    contractStartDate: relDate(30),
    addons: []
  },
  {
    id: 8875,
    contractId: 4990,
    commercialPlanId: 700,
    name: 'Wellness Program',
    productType: 'wellness',
    status: 'active',
    price: 18,
    currencyCode: 'USD',
    coBranded: true,
    numberOfLicenses: 200,
    quoteId: 1290,
    contractStartDate: relDate(-365),
    addons: []
  }
];

export const GET_CLIENT_PLANS_MOCK_RESPONSE = (): Promise<{ data: IClientPlansResponse }> =>
  Promise.resolve({ data: { plans: CLIENT_PLANS_MOCK } });

export const CLIENT_CONTACTS_API_MOCK: IClientContact[] = [
  {
    id: 1,
    name: 'Ana García',
    email: 'ana.garcia@techsolutions.cl',
    phone: '+56 9 1234 5678',
    contactSource: 'client',
    contactTypes: [ClientContactType.Administrator]
  },
  {
    id: 2,
    name: 'Bruno Díaz',
    email: 'bruno.diaz@techsolutions.cl',
    phone: '+56 9 2345 6789',
    contactSource: 'contract',
    contactTypes: [ClientContactType.PaymentManager]
  },
  {
    id: 3,
    name: 'Carla Muñoz',
    email: 'carla.munoz@techsolutions.cl',
    phone: '+56 9 3456 7890',
    contactSource: 'client',
    contactTypes: [ClientContactType.Administrator, ClientContactType.PaymentManager]
  },
  {
    id: 4,
    name: 'Diego Rojas',
    email: 'diego.rojas@techsolutions.cl',
    contactSource: 'contract',
    contactTypes: [ClientContactType.Administrator]
  }
];

export const GET_CLIENT_CONTACTS_MOCK_RESPONSE = (): Promise<{ data: IClientContactsResponse }> =>
  Promise.resolve({ data: { contacts: CLIENT_CONTACTS_API_MOCK } });
